import React, { useMemo } from 'react';
import { Grid, Typography, Box, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import PsiListItem from '@/features/psi/PsiListItem.tsx';
import { ListInvestorCollaborationsDTO } from '@inversor-hub/types';
import { PsiCardCollaborationStatusLabel } from './PsiCardCollaborationStatusLabel.tsx';

export interface MyCollaborationsProps {
  collaborations: ListInvestorCollaborationsDTO[];
  onOpenContract: (collabId: string) => void;
  searchTerm?: string;
}

export const MyPsiCollaborations: React.FC<MyCollaborationsProps> = ({
  collaborations,
  onOpenContract,
  searchTerm = '',
}) => {
  const navigate = useNavigate();

  const filteredCollaborations = useMemo(() => {
    if (!searchTerm) return collaborations;
    return collaborations.filter((c) => {
      const psi = c.psi!;
      const lowerSearch = searchTerm.toLowerCase();

      const fieldsToSearch = [psi.name, psi.province, psi.city];
      return fieldsToSearch.some((v) => v && v.toString().toLowerCase().includes(lowerSearch));
    });
  }, [collaborations, searchTerm]);

  const hasCollaborations = collaborations.length > 0;
  const hasFilteredResults = filteredCollaborations.length > 0;
  const isFiltering = searchTerm.length > 0;

  // No collaborations at all
  if (!hasCollaborations) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No tienes colaboraciones activas con PSIs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Busca oportunidades de inversión para comenzar a colaborar con un PSI
        </Typography>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => navigate('/oportunidades')}
        >
          Explorar oportunidades
        </Button>
      </Box>
    );
  }

  // Has collaborations but no search results
  if (!hasFilteredResults && isFiltering) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No se encontraron resultados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Intenta con otros términos de búsqueda
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {filteredCollaborations.map((collab) => {
        const { psi } = collab;
        return (
          <PsiListItem
            key={collab.id}
            psi={psi}
            label={
              <PsiCardCollaborationStatusLabel
                psiId={psi.id}
                collaborations={collaborations}
                onOpenContract={onOpenContract}
              />
            }
          />
        );
      })}
    </Grid>
  );
};
